import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { Palette, Sun, Moon, Monitor, Check, Maximize2, Minimize2, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Density } from '@/hooks/useTheme';

const themes = [
  { id: 'zinc' as const, name: 'Zinc', color: 'hsl(240 5.9% 10%)' },
  { id: 'blue' as const, name: 'Blue', color: 'hsl(221 83% 53%)' },
  { id: 'green' as const, name: 'Green', color: 'hsl(142 71% 45%)' },
  { id: 'orange' as const, name: 'Orange', color: 'hsl(24 95% 53%)' },
  { id: 'rose' as const, name: 'Rose', color: 'hsl(347 77% 50%)' },
  { id: 'violet' as const, name: 'Violet', color: 'hsl(262 83% 58%)' },
  { id: 'teal' as const, name: 'Teal', color: 'hsl(174 63% 40%)' },
  { id: 'amber' as const, name: 'Amber', color: 'hsl(45 100% 51%)' },
  { id: 'indigo' as const, name: 'Indigo', color: 'hsl(243 75% 59%)' },
  { id: 'cyan' as const, name: 'Cyan', color: 'hsl(192 92% 48%)' },
  { id: 'lime' as const, name: 'Lime', color: 'hsl(90 70% 50%)' },
  { id: 'pink' as const, name: 'Pink', color: 'hsl(327 70% 50%)' },
  { id: 'brown' as const, name: 'Brown', color: 'hsl(25 30% 40%)' },  
];

/**
 * Unified ThemeSelector component that combines appearance mode,
 * color theme, and density settings in a single dropdown.
 */
export function ThemeSelector() {
  const { t } = useTranslation('common');
  const { mode, setMode, colorTheme, setColorTheme, density, setDensity } = useTheme();

  const currentTheme = themes.find(t => t.id === colorTheme) || themes[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          aria-label={t('theme.changeTheme')}
        >
          <Palette
            className="h-4 w-4"
            style={{ color: currentTheme.color }}
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 max-w-[95vw]">
        {/* Appearance Mode Section */}
        <DropdownMenuLabel>{t('theme.mode.label')}</DropdownMenuLabel>
        <div className="grid grid-cols-2 gap-1.5 p-2">
          <Button
            onClick={() => setMode('light')}
            variant={mode === 'light' ? 'default' : 'outline'}
            size="sm"
            className="min-w-0"
          >
            <Sun className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('theme.mode.light')}</span>
          </Button>
          <Button
            onClick={() => setMode('dark')}
            variant={mode === 'dark' ? 'default' : 'outline'}
            size="sm"
            className="min-w-0"
          >
            <Moon className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('theme.mode.dark')}</span>
          </Button>
          <Button
            onClick={() => setMode('system')}
            variant={mode === 'system' ? 'default' : 'outline'}
            size="sm"
            className="min-w-0"
          >
            <Monitor className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('theme.mode.system')}</span>
          </Button>
          <Button
            onClick={() => setMode('public-media')}
            variant={mode === 'public-media' ? 'default' : 'outline'}
            size="sm"
            className="min-w-0"
          >
            <Radio className="h-4 w-4 sm:mr-2" aria-hidden="true" />
            <span className="hidden sm:inline">{t('theme.mode.publicMedia')}</span>
          </Button>
        </div>

        <DropdownMenuSeparator />

        {/* Color Theme Section */}
        <DropdownMenuLabel>{t('theme.color.label')}</DropdownMenuLabel>
        <div className="grid grid-cols-6 gap-2 p-2">
          {themes.map(theme => (
            <button
              key={theme.id}
              onClick={() => setColorTheme(theme.id)}
              className="relative w-10 h-10 rounded-full border-2 hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
              style={{
                backgroundColor: theme.color,
                borderColor: colorTheme === theme.id ? 'hsl(var(--ring))' : 'transparent'
              }}
              aria-label={t(`theme.color.${theme.id}`)}
              title={t(`theme.color.${theme.id}`)}
            >
              {colorTheme === theme.id && (
                <Check className="h-5 w-5 text-white absolute inset-0 m-auto" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Density Section */}
        <DropdownMenuLabel>{t('theme.density.label')}</DropdownMenuLabel>
          <div className="flex gap-1.5 p-2 mb-2" aria-label="Segmented Toggle">
            {[{id:'comfortable',icon:<Maximize2 className="h-4 w-4"/>,label:t('theme.density.comfortable')},{id:'compact',icon:<Minimize2 className="h-4 w-4"/>,label:t('theme.density.compact')},{id:'crowded',icon:<Minimize2 className="h-4 w-4 rotate-45"/>,label:t('theme.density.crowded')}].map(opt => (
              <button
                key={opt.id}
                onClick={()=>setDensity(opt.id as Density)}
                className={`flex-1 flex flex-col items-center justify-center px-2 py-1 rounded-lg border transition-all duration-150 ${density===opt.id?'bg-primary text-primary-foreground border-primary scale-105 shadow':'bg-muted text-muted-foreground border-transparent'} hover:scale-105 focus:outline-none`}
                aria-label={opt.label}
              >
                {opt.icon}
                <span className="text-xs mt-1">{opt.label}</span>
              </button>
            ))}
          </div>


      </DropdownMenuContent>
    </DropdownMenu>
  );
}
